import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { inquiryService } from '../services/api';

export default function InquiryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState('');
  
  // Editable fields
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Attachment state
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [attachmentMessage, setAttachmentMessage] = useState({ type: '', text: '' });

  // Email composer state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');

  useEffect(() => {
    loadInquiry();
  }, [id]);

  const loadInquiry = async () => {
    try {
      setLoading(true);
      const data = await inquiryService.getById(id);
      setInquiry(data);
      setStatus(data.status);
      setNotes(data.notes || '');

      // Load attachments
      try {
        const atts = await inquiryService.getAttachments(id);
        setAttachments(atts);
      } catch (attErr) {
        console.error('Error loading attachments:', attErr);
      }
    } catch (err) {
      console.error('Error loading inquiry:', err);
      setError('Failed to load inquiry');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
    setHasChanges(true);
  };

  const handleNotesChange = (e) => {
    setNotes(e.target.value);
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const updated = await inquiryService.update(id, { status, notes });
      setInquiry({ ...inquiry, ...updated });
      setHasChanges(false);
    } catch (err) {
      console.error('Error saving inquiry:', err);
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleConvertToClient = async () => {
    if (!window.confirm('Convert this inquiry to a client? This will create a new client record.')) {
      return;
    }
    
    try {
      setConverting(true);
      setError('');
      const client = await inquiryService.convertToClient(id);
      // Reload inquiry to get updated status and client reference
      await loadInquiry();
      alert(`Client "${client.name}" created successfully!`);
    } catch (err) {
      console.error('Error converting to client:', err);
      if (err.response?.status === 400) {
        setError('This inquiry has already been converted to a client');
      } else {
        setError('Failed to convert inquiry to client');
      }
    } finally {
      setConverting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this inquiry? This action cannot be undone.')) {
      return;
    }
    
    try {
      await inquiryService.delete(id);
      navigate('/inquiries');
    } catch (err) {
      console.error('Error deleting inquiry:', err);
      setError('Failed to delete inquiry');
    }
  };

  // Email composer handlers
  const openEmailComposer = () => {
    setEmailSubject(`Re: Your inquiry to CR Hood Solutions`);
    setEmailMessage(`Hi ${inquiry.name},\n\nThank you for reaching out to CR Hood Solutions.\n\n`);
    setEmailSuccess('');
    setShowEmailModal(true);
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    
    if (!emailSubject.trim() || !emailMessage.trim()) {
      setError('Please enter both subject and message');
      return;
    }

    try {
      setSendingEmail(true);
      setError('');
      const result = await inquiryService.sendEmail(id, emailSubject, emailMessage);
      
      // Update local state with the returned inquiry (includes updated notes and status)
      if (result.inquiry) {
        setInquiry(result.inquiry);
        setNotes(result.inquiry.notes || '');
        setStatus(result.inquiry.status);
      }
      
      setEmailSuccess('Email sent successfully!');
      setTimeout(() => {
        setShowEmailModal(false);
        setEmailSubject('');
        setEmailMessage('');
        setEmailSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Error sending email:', err);
      setError('Failed to send email. Please check your SendGrid configuration.');
    } finally {
      setSendingEmail(false);
    }
  };

  // Attachment handlers
  const uploadAbortRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';

    if (file.size > 10 * 1024 * 1024) {
      setAttachmentMessage({ type: 'error', text: 'File exceeds the 10MB size limit.' });
      return;
    }

    const abortController = new AbortController();
    uploadAbortRef.current = abortController;

    setUploading(true);
    setAttachmentMessage({ type: '', text: '' });

    try {
      const newAttachment = await inquiryService.uploadAttachment(id, file, {
        signal: abortController.signal,
      });
      setAttachments([...attachments, newAttachment]);
      setAttachmentMessage({ type: 'success', text: `"${file.name}" uploaded successfully.` });
    } catch (error) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setAttachmentMessage({ type: 'error', text: 'Upload timed out. Please try again.' });
      } else if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        setAttachmentMessage({ type: 'error', text: 'Upload canceled.' });
      } else {
        console.error('Error uploading file:', error);
        const errMsg = error.response?.data?.message || error.response?.data || 'Error uploading file.';
        setAttachmentMessage({ type: 'error', text: typeof errMsg === 'string' ? errMsg : 'Error uploading file.' });
      }
    } finally {
      uploadAbortRef.current = null;
      setUploading(false);
    }
  };

  const handleCancelUpload = () => {
    if (uploadAbortRef.current) {
      uploadAbortRef.current.abort();
    }
  };

  const handleDeleteAttachment = async (attachmentId, filename) => {
    if (!confirm(`Delete attachment "${filename}"? This cannot be undone.`)) return;

    try {
      await inquiryService.deleteAttachment(id, attachmentId);
      setAttachments(attachments.filter(a => a.id !== attachmentId));
      setAttachmentMessage({ type: 'success', text: `"${filename}" deleted.` });
    } catch (error) {
      console.error('Error deleting attachment:', error);
      setAttachmentMessage({ type: 'error', text: 'Error deleting attachment.' });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusBadge = (statusValue) => {
    const badges = {
      new: 'badge-info',
      contacted: 'badge-warning',
      qualified: 'badge-success',
      closed: 'badge-gray',
    };
    return badges[statusValue] || 'badge-gray';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!inquiry) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-500">Inquiry not found</p>
        <Link to="/inquiries" className="text-accent hover:underline mt-4 inline-block">
          Back to Inquiries
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Inquiry: {inquiry.name} | CR Hood Admin</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/inquiries"
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{inquiry.name}</h1>
              <p className="text-sm text-gray-500">
                Submitted {formatDate(inquiry.createdAt)}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn btn-primary"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
            <button
              onClick={handleDelete}
              className="btn btn-danger"
            >
              Delete
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Information */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-500">Name</label>
                  <p className="font-medium">{inquiry.name}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Email</label>
                  <p className="font-medium">
                    <a
                      href={`mailto:${inquiry.email}`}
                      className="text-accent hover:underline"
                    >
                      {inquiry.email}
                    </a>
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Phone</label>
                  <p className="font-medium">
                    {inquiry.phone ? (
                      <a href={`tel:${inquiry.phone}`} className="text-accent hover:underline">
                        {inquiry.phone}
                      </a>
                    ) : (
                      <span className="text-gray-400">Not provided</span>
                    )}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Company</label>
                  <p className="font-medium">
                    {inquiry.company || <span className="text-gray-400">Not provided</span>}
                  </p>
                </div>
              </div>
            </div>

            {/* Message */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Message</h2>
              <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap text-gray-700">
                {inquiry.message}
              </div>
            </div>

            {/* Notes */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Internal Notes</h2>
              <textarea
                value={notes}
                onChange={handleNotesChange}
                rows={5}
                className="form-input"
                placeholder="Add notes about this inquiry, follow-up actions, etc."
              />
              <p className="text-xs text-gray-500 mt-2">
                Notes are private and will not be visible to the contact.
              </p>
            </div>

            {/* Attachments */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">
                Attachments
                {attachments.length > 0 && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({attachments.length} file{attachments.length !== 1 ? 's' : ''})
                  </span>
                )}
              </h2>

              {attachmentMessage.text && (
                <div
                  className={`p-3 rounded-lg mb-4 text-sm ${
                    attachmentMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}
                >
                  {attachmentMessage.text}
                </div>
              )}

              {attachments.length > 0 && (
                <div className="space-y-2 mb-4">
                  {attachments.map((att) => (
                    <div
                      key={att.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{att.filename}</p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(att.fileSize)} &middot; {new Date(att.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <a
                          href={inquiryService.downloadAttachmentUrl(id, att.id)}
                          className="text-accent hover:text-accent-dark text-sm"
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Download"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </a>
                        <button
                          type="button"
                          onClick={() => handleDeleteAttachment(att.id, att.filename)}
                          className="text-red-400 hover:text-red-600 text-sm"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div>
                <div className="flex items-center gap-2">
                  <label className="btn btn-secondary text-sm cursor-pointer inline-flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    {uploading ? 'Uploading...' : 'Upload File'}
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv,.zip"
                    />
                  </label>
                  {uploading && (
                    <button
                      type="button"
                      onClick={handleCancelUpload}
                      className="text-sm text-red-600 hover:text-red-800 underline"
                    >
                      Cancel
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Max 10MB per file. Accepted: PDF, Word, Excel, PowerPoint, images, text, CSV, ZIP.
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Status</h2>
              <div className="space-y-2">
                {['new', 'contacted', 'qualified', 'closed'].map((statusOption) => (
                  <button
                    key={statusOption}
                    onClick={() => handleStatusChange(statusOption)}
                    className={`w-full text-left px-4 py-2 rounded-lg border transition-colors ${
                      status === statusOption
                        ? 'border-accent bg-accent/5 text-accent'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <span className={`badge ${getStatusBadge(statusOption)} mr-2`}>
                      {statusOption}
                    </span>
                    <span className="text-xs text-gray-500 capitalize">
                      {statusOption === 'new' && '— Not yet contacted'}
                      {statusOption === 'contacted' && '— Reached out'}
                      {statusOption === 'qualified' && '— Potential client'}
                      {statusOption === 'closed' && '— No longer active'}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Convert to Client */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Client Conversion</h2>
              {inquiry.client ? (
                <div>
                  <p className="text-sm text-gray-600 mb-3">
                    This inquiry has been converted to a client.
                  </p>
                  <Link
                    to={`/clients/${inquiry.client.id || inquiry.client}`}
                    className="btn btn-secondary w-full"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    View Client
                  </Link>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-3">
                    Convert this inquiry to a client record to track proposals, engagements, and invoices.
                  </p>
                  <button
                    onClick={handleConvertToClient}
                    disabled={converting}
                    className="btn btn-primary w-full"
                  >
                    {converting ? (
                      <>
                        <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Converting...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                        </svg>
                        Convert to Client
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
              <div className="space-y-2">
                <button
                  onClick={openEmailComposer}
                  className="btn btn-primary w-full"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send Email
                </button>
                {inquiry.phone && (
                  <a
                    href={`tel:${inquiry.phone}`}
                    className="btn btn-secondary w-full"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Call
                  </a>
                )}
                <a
                  href={`mailto:${inquiry.email}?subject=Re: Your inquiry to CR Hood Solutions`}
                  className="btn btn-secondary w-full text-xs"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Open in Email App
                </a>
              </div>
            </div>

            {/* Metadata */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Details</h2>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-gray-500">Inquiry ID</dt>
                  <dd className="font-mono text-gray-900">#{inquiry.id}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Source</dt>
                  <dd className="text-gray-900 capitalize">{inquiry.source}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Received</dt>
                  <dd className="text-gray-900">{formatDate(inquiry.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Last Updated</dt>
                  <dd className="text-gray-900">{formatDate(inquiry.updatedAt)}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Email Composer Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Send Email</h2>
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {emailSuccess ? (
                <div className="text-center py-8">
                  <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-lg font-medium text-green-600">{emailSuccess}</p>
                </div>
              ) : (
                <form onSubmit={handleSendEmail}>
                  <div className="space-y-4">
                    {/* To */}
                    <div>
                      <label className="form-label">To</label>
                      <input
                        type="text"
                        value={`${inquiry.name} <${inquiry.email}>`}
                        disabled
                        className="form-input bg-gray-50"
                      />
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="form-label">Subject *</label>
                      <input
                        type="text"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        className="form-input"
                        required
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label className="form-label">Message *</label>
                      <textarea
                        value={emailMessage}
                        onChange={(e) => setEmailMessage(e.target.value)}
                        rows={10}
                        className="form-input font-mono text-sm"
                        required
                      />
                    </div>

                    {/* Original Message Reference */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-500 mb-2">Original inquiry:</p>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{inquiry.message}</p>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowEmailModal(false)}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={sendingEmail}
                      className="btn btn-primary"
                    >
                      {sendingEmail ? (
                        <>
                          <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                          Send Email
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
